import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Calendar, Clock, Shield, CheckCircle, Users, Award } from 'lucide-react';
import LandingNav from '../../components/Landing-Nav';
import FooterLanding from '../../components/FooterLanding';

const HealthcareIndustry = () => {
  const features = [
    {
      icon: Calendar,
      title: 'HIPAA-Compliant Scheduling',
      description: 'Ensure patient data security with fully compliant workforce management'
    },
    {
      icon: Users,
      title: 'Multi-Department Coordination',
      description: 'Seamlessly manage nurses, doctors, technicians, and support staff'
    },
    {
      icon: Clock,
      title: '24/7 Shift Coverage',
      description: 'Never miss a shift with intelligent automated scheduling'
    },
    {
      icon: Shield,
      title: 'Credential Tracking',
      description: 'Automated license and certification monitoring'
    }
  ];

  const benefits = [
    '40% reduction in scheduling time',
    '25% decrease in overtime costs',
    '95% staff satisfaction rate',
    'HIPAA-compliant data security',
    'Real-time staff availability',
    'Automated shift reminders'
  ];

  const stats = [
    { value: '500+', label: 'Healthcare Facilities' },
    { value: '40%', label: 'Time Saved' },
    { value: '95%', label: 'Satisfaction Rate' },
    { value: '24/7', label: 'Support' }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <Heart className="w-8 h-8" />
                <span className="text-blue-200 font-semibold">Healthcare & Medical Facilities</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Patient Care Starts with Proper Staff Management
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Optimize staff scheduling and patient care coordination with HIPAA-compliant, secure workforce management software. Ensure your healthcare teams are properly staffed across all shifts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/signup"
                  className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all text-center"
                >
                  Schedule a Demo
                </Link>
                <Link 
                  to="/pricing"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all text-center"
                >
                  View Pricing
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img 
                src="/Admin Dashboard.png" 
                alt="Healthcare Dashboard"
                className="rounded-2xl shadow-2xl border-4 border-white/20"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Purpose-Built for Healthcare
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized features designed to meet the unique challenges of healthcare workforce management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <img 
                src="/Schedule Page.png" 
                alt="Healthcare Scheduling"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Measurable Results for Healthcare Organizations
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 text-blue-600 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            "Shiftry transformed our staff scheduling process. We've reduced overtime costs by 25% while improving staff satisfaction significantly."
          </blockquote>
          <div className="text-lg text-gray-600">
            <p className="font-semibold">Dr. Sarah Johnson</p>
            <p>Chief Nursing Officer, City General Hospital</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Healthcare Operations?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of healthcare facilities already using Shiftry
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Schedule Your Free Demo
            </Link>
            <Link 
              to="/pricing"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default HealthcareIndustry;
